import type { SEOArticle } from './seoBlogArticles'

export const part3Articles: SEOArticle[] = [
  {
    slug: 'de-vier-vissers-welke-prospect-is-jouw-prospect',
    title: 'De vier vissers: welke ondernemer in jouw markt is welke visser?',
    cluster: 'CorePrint Methodiek',
    metaDescription:
      'In de CorePrint methodiek herkennen we vier typen ondernemers in elke markt: de hengelaar, de kreeftvanger, de optimist en de visser met sonar. Een gids om jouw eigen positie te bepalen.',
    content: [
      { type: 'heading', content: 'Vier typen ondernemers op dezelfde oceaan' },
      {
        type: 'paragraph',
        content:
          'Elke markt is een oceaan. De vis (jouw potentiële cliënten) zwemt er rond, en in dezelfde oceaan vissen vier typen ondernemers. Ze hebben verschillende methodes, verschillende resultaten, en heel verschillende verhoudingen tussen inspanning en resultaat. Welke visser ben jij vandaag? En welke wil je over een jaar zijn?',
      },
      {
        type: 'callout',
        content:
          'De keuze tussen netwerk-afhankelijkheid en data-gestuurde groei is een keuze tussen vier specifieke vissers. Kies bewust.',
      },
      { type: 'heading3', content: 'De hengelaar: leeft van geluk en netwerk' },
      {
        type: 'paragraph',
        content:
          'De hengelaar heeft geen website, of een verouderde site uit 2018. Hij vist met hengel en aas, één vis tegelijk, en wacht tot er iets toehapt. Zijn opdrachten komen via netwerk, doorverwijzingen, en geluk. Het werkt, een tijdje. Tot het netwerk uitgeput is, een grote doorverwijzer met pensioen gaat, of de markt verandert. Dan staat hij stil. De hengelaar denkt: "Ik heb het altijd zo gedaan, het werkt toch?". Tot het stopt te werken.',
      },
      { type: 'heading3', content: 'De kreeftvanger: een klein netje op één plek' },
      {
        type: 'paragraph',
        content:
          "De kreeftvanger heeft een website van vijf tot tien pagina's. Een homepage, dienstenpagina's, een over-ons en een contactformulier. Het is een kleine kreeftnet op één plek in de oceaan. Soms zwemt er een mooie vis in, dat heet dan een aanvraag via de site. De kans is klein, maar als het gebeurt is hij blij. Hij heeft geen idee waar de vis precies vandaan kwam en of er meer waren. Het werkt, maar het schaalt niet.",
      },
      { type: 'heading3', content: 'De optimist met sleepnet: groot net op een lege plek' },
      {
        type: 'paragraph',
        content:
          "De optimist heeft een SEO-bureau ingehuurd dat een groot sleepnet voor hem heeft gebouwd. Honderden pagina's, mooi gestructureerd, met alle juiste zoekwoorden volgens de keyword-tool. Hij betaalt elke maand 800 tot 2000 euro voor onderhoud en uitbreiding. Het ziet er professioneel uit. Maar de vangst valt tegen. Het net is groot, maar het hangt op een plek waar de vis niet zwemt. Niemand heeft van tevoren gemeten of die zoekwoorden ook daadwerkelijk klanten opleveren.",
      },
      { type: 'heading3', content: 'De visser met sonar: eerst meten, dan vissen' },
      {
        type: 'paragraph',
        content:
          'De vierde visser doet het anders. Voordat hij het sleepnet uitzet, gebruikt hij sonar om te peilen waar de vis daadwerkelijk zwemt. Dat is de CorePrint-aanpak: 90 dagen meten met gerichte advertenties welke zoekwoorden in jouw markt converteren, en pas dan een SEO-silo bouwen op die bewezen visstand. Zijn investering is in het begin vergelijkbaar met de optimist, maar zijn resultaten zijn structureel beter. En naarmate de organische posities groeien, kan zijn budget krimpen.',
      },
      {
        type: 'callout',
        content:
          'Sonar is geen luxe. Voor een klein bedrijf met beperkt budget is sonar het verschil tussen verspilling en investering.',
      },
      { type: 'heading3', content: 'Welke visser ben jij vandaag?' },
      {
        type: 'paragraph',
        content:
          'De meeste zakelijke dienstverleners die we ontmoeten zijn een hengelaar of kreeftvanger. Een kleinere groep is een optimist die ooit een dure SEO-aanpak kocht zonder resultaat. Bijna niemand is een visser met sonar, omdat de meeste bureaus deze aanpak niet aanbieden. En precies daardoor is het zo waardevol om er een te worden. Je krijgt structureel voorsprong op een markt vol vissers die op geluk varen.',
      },
    ],
    relatedArticles: [
      'wat-is-coreprint-methodiek',
      'waarom-google-ads-data-de-beste-seo-onderbouwing-is',
      'data-gedreven-marketing-mkb',
    ],
  },
  {
    slug: 'waarom-google-ads-data-de-beste-seo-onderbouwing-is',
    title: 'Waarom Google Ads data de beste onderbouwing is voor SEO',
    cluster: 'CorePrint Methodiek',
    metaDescription:
      'Keyword-tools geven volume, maar geen bewijs. Google Ads draait één keer en geeft harde conversiedata. Waarom 90 dagen advertentiedata de beste basis is voor je SEO-strategie.',
    content: [
      { type: 'heading', content: 'Het verschil tussen volume en bewijs' },
      {
        type: 'paragraph',
        content:
          'Bijna elk SEO-bureau begint met keyword-onderzoek via tools als Ahrefs, SEMrush of Google Keyword Planner. Die tools laten zien hoeveel mensen per maand een bepaald woord zoeken. Het probleem is dat zoekvolume niets zegt over of die zoekers ook klanten worden. Een zoekwoord met 5000 zoekopdrachten per maand kan nul klanten opleveren. Een zoekwoord met 50 zoekopdrachten kan je beste leadbron worden. En een keyword-tool kan dat verschil niet zien.',
      },
      {
        type: 'callout',
        content:
          'Volume is wat zoekers doen. Conversie is wat ze willen. Alleen Google Ads laat het verschil zien.',
      },
      { type: 'heading3', content: 'Wat Google Ads daadwerkelijk meet' },
      {
        type: 'paragraph',
        content:
          'Wanneer je een Google Ads campagne draait, meet je niet alleen wie er klikt, maar ook wie er converteert. Een formulier invult, een telefoontje pleegt, een gesprek boekt. Per zoekwoord, dag in dag uit. Na 90 dagen weet je voor elk zoekwoord in je campagne hoeveel klikken het kreeg, hoeveel conversies dat opleverde, en wat een lead je kostte. Dat is geen aanname, dat is meetbare realiteit in jouw specifieke markt.',
      },
      { type: 'heading3', content: 'Waarom dit beter is dan keyword-onderzoek' },
      {
        type: 'list',
        items: [
          'Het meet jouw markt, niet de gemiddelde markt: zoekvolume in een keyword-tool is een landelijk of regionaal gemiddelde. Jouw markt kan totaal anders zijn.',
          'Het meet conversie, niet kliks: 1000 klikken zonder conversie is nul waarde. 30 klikken met 5 conversies is een goudmijn.',
          'Het meet seizoenseffecten: een 90-dagen campagne dekt een heel kwartaal en laat zien welke zoekwoorden seizoensafhankelijk zijn.',
          'Het meet de werkelijke intentie: bepaalde zoekwoorden ogen veelbelovend in een tool, maar de zoeker blijkt iets anders te willen dan jij aanbiedt. Dat zie je pas als je daadwerkelijk klanten meet.',
        ],
      },
      { type: 'heading3', content: 'Waarom niet iedereen het zo doet' },
      {
        type: 'paragraph',
        content:
          'De voor de hand liggende vraag: als deze aanpak zoveel beter is, waarom doet niet iedereen het zo? Het antwoord is praktisch. Google Ads vereist budget vooraf (advertentiekosten plus campagnebeheer), specifieke expertise, en geduld voor de meting. De meeste SEO-bureaus verkopen liever direct het sleepnet, omdat dat sneller geld oplevert dan eerst 90 dagen meten. Voor de klant zijn de extra kosten en tijd vooraf goed besteed, omdat het de SEO-investering daarna stuurt richting bewezen converters.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw kantoor' },
      {
        type: 'paragraph',
        content:
          'Voordat je een meerjarig SEO-contract tekent, vraag of de aanbieder eerst meet welke zoekwoorden converteren. Als het antwoord is "dat doen we met keyword-tools", weet je dat je een sleepnet koopt op een plek die niemand heeft onderzocht. Als het antwoord is "we draaien eerst 90 dagen advertenties om te valideren", dan koop je een systeem dat is gebouwd op bewijs.',
      },
    ],
    relatedArticles: [
      'wat-is-coreprint-methodiek',
      'de-vier-vissers-welke-prospect-is-jouw-prospect',
      'coreprint-fase-1-tot-3-wat-gebeurt-er-in-90-dagen',
    ],
  },
  {
    slug: 'coreprint-fase-1-tot-3-wat-gebeurt-er-in-90-dagen',
    title: 'Wat gebeurt er in 90 dagen CorePrint: een blik onder de motorkap',
    cluster: 'CorePrint Methodiek',
    metaDescription:
      'Wat gebeurt er precies in de eerste 90 dagen van een CorePrint-traject? Een stap voor stap uitleg, met wat we meten, wat we leveren, en wat dat voor jouw markt betekent.',
    content: [
      { type: 'heading', content: 'De drie fases die het verschil maken' },
      {
        type: 'paragraph',
        content:
          'CorePrint draait om volgorde. Eerst meten, dan bouwen, dan afbouwen van advertenties. De eerste 90 dagen zijn de fundering, en als die niet zorgvuldig wordt gelegd, klopt de rest ook niet. Hieronder leggen we uit wat er in die drie maanden gebeurt en wat het oplevert.',
      },
      {
        type: 'callout',
        content:
          'De eerste 90 dagen zijn geen wachtperiode. Het zijn de belangrijkste 90 dagen van je hele marketingaanpak.',
      },
      { type: 'heading3', content: 'Fase 1, dagen 1 tot 21: intake en campagne-opzet' },
      {
        type: 'paragraph',
        content:
          'In de eerste drie weken brengen we jouw markt in kaart. Welke diensten lever je, voor wie, en in welke regio? Welke zoekwoorden lijken op het eerste oog relevant? Welke landingspagina hoort bij welk zoekwoord? We bouwen een eerste set van twintig tot dertig advertenties en starten de campagne. Vanaf dag 21 staat er een meetinstrument in jouw markt te draaien dat conversies registreert.',
      },
      { type: 'heading3', content: 'Fase 2, dagen 21 tot 75: meten, bijsturen, meten' },
      {
        type: 'paragraph',
        content:
          'Wekelijks kijken we naar de data. Welke zoekwoorden krijgen kliks, welke krijgen conversies, welke kosten te veel per lead. Onbruikbare zoekwoorden zetten we uit, kansrijke breiden we uit met varianten. Slecht presterende advertentieteksten herschrijven we, goedlopende advertenties geven we meer budget. Dit is dynamisch werk, en het is essentieel om de data zuiver te maken.',
      },
      { type: 'heading3', content: 'Fase 3, dagen 75 tot 90: rapportage en silo-blauwdruk' },
      {
        type: 'paragraph',
        content:
          'In de laatste twee weken leveren we het CorePrint-rapport. Dat document bevat:',
      },
      {
        type: 'list',
        items: [
          'De tien tot vijftien zoekwoorden in jouw markt met het hoogste conversiepercentage.',
          'De zoekwoorden die volume hadden maar nul conversies opleverden (deze ga je niet opnemen in je SEO-strategie).',
          'De kosten per lead per zoekwoord, en daarmee de waarde van organische posities op die woorden.',
          "Een silo-blauwdruk: welke pagina's je gaat schrijven, in welke onderlinge structuur, en met welke prioriteit.",
        ],
      },
      { type: 'heading3', content: 'En daarna?' },
      {
        type: 'paragraph',
        content:
          'Vanaf dag 91 begint het bouwwerk op bewijs. Pagina voor pagina wordt de silo opgezet op de gevalideerde converters. Tegelijk blijven we de advertentiecampagne draaien, maar nu op de bewezen converters in plaats van op breed verkennen. Naarmate je organische posities op die zoekwoorden stijgen, kan het advertentiebudget afgebouwd worden. Wat begint als een fase van investeren in meten, wordt een systeem van structureel gratis verkeer.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw kantoor' },
      {
        type: 'paragraph',
        content:
          'Plan een vrijblijvend gesprek waarin we kijken naar jouw markt, de zoekvragen die daar relevant lijken, en welke investering past bij jouw groei-ambitie. Aan het einde van dat gesprek weet je niet alleen of CorePrint bij je past, maar ook welke eerste meetcampagne we voor jou zouden opzetten.',
      },
    ],
    relatedArticles: [
      'wat-is-coreprint-methodiek',
      'waarom-google-ads-data-de-beste-seo-onderbouwing-is',
      'wat-kost-seo-zakelijke-dienstverlener',
    ],
  },
  {
    slug: 'seo-bureau-kiezen-7-vragen',
    title: 'SEO-bureau kiezen: 7 vragen die je moet stellen voor je tekent',
    cluster: 'CorePrint Methodiek',
    metaDescription:
      'Een SEO-traject is een meerjarige investering. Zeven concrete vragen die elke zakelijke dienstverlener moet stellen voordat hij een bureau kiest, met de antwoorden die je wel en niet wilt horen.',
    content: [
      { type: 'heading', content: 'De gesprekken die het verschil maken tussen 12 maanden winst en 12 maanden verlies' },
      {
        type: 'paragraph',
        content:
          'Een SEO-traject kost gemiddeld 800 tot 1600 euro per maand, voor minimaal twaalf maanden. Dat is een investering van tien- tot twintigduizend euro. Voor die investering kies je een bureau in een gesprek van een uur. Het loont om dat gesprek zorgvuldig te voeren. Hieronder de zeven vragen die het verschil maken tussen een bureau dat resultaten levert en een bureau dat alleen rapporten levert.',
      },
      {
        type: 'callout',
        content:
          'Een goed bureau wordt enthousiast van deze vragen. Een slecht bureau wordt ongemakkelijk.',
      },
      { type: 'heading3', content: 'De zeven vragen, met wat je wel en niet wilt horen' },
      {
        type: 'list',
        items: [
          '1. Hoe weten jullie welke zoekwoorden in mijn markt converteren? Goed antwoord: "We meten dat eerst met advertenties voordat we SEO-content bouwen." Slecht antwoord: "We doen keyword-onderzoek met onze tools."',
          '2. Mag ik referenties bellen van kantoren in mijn sector? Goed antwoord: "Ja, hier zijn drie kantoren in jouw branche, bel ze gerust." Slecht antwoord: "We werken eigenlijk niet veel in jouw sector."',
          '3. Wie schrijft de content? Goed antwoord: "Onze eigen redacteur of een externe schrijver met domeinkennis, en jij krijgt elk artikel ter goedkeuring." Slecht antwoord: "We gebruiken AI voor de basisteksten."',
          '4. Wat doen jullie als de resultaten achterblijven? Goed antwoord: "Dan bespreken we het in onze maandelijkse meeting en passen we de strategie aan." Slecht antwoord: "SEO duurt nu eenmaal even."',
          '5. Hoe meten jullie het succes van het traject? Goed antwoord: "Aan het aantal kwalitatieve aanvragen via de site, niet aan het aantal bezoekers." Slecht antwoord: "We sturen je elke maand een rankings-rapport."',
          '6. Wat is mijn opzegtermijn? Goed antwoord: "Maand op maand na een eerste opbouwperiode van zes maanden." Slecht antwoord: "24 maanden vast, anders werkt SEO niet."',
          '7. Wie heeft eigendom van de content en de zoekdata na afloop? Goed antwoord: "Alles wat we bouwen is van jou, ook de campagnedata." Slecht antwoord: "Dat blijft op onze accounts."',
        ],
      },
      { type: 'heading3', content: 'De rode vlaggen die je niet mag missen' },
      {
        type: 'paragraph',
        content:
          'Naast deze zeven vragen zijn er een paar signalen die altijd alarmbellen moeten doen rinkelen. Garanties op posities op specifieke zoekwoorden (kan niemand geven). Een vast pakket dat "voor iedereen werkt" (een goed traject is altijd op maat). Of een bureau dat alleen op zoekvolume stuurt zonder over conversies te praten. Bij elk van deze signalen is een tweede gesprek nodig om door te vragen.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw kantoor' },
      {
        type: 'paragraph',
        content:
          'Voordat je het volgende offertegesprek voert, schrijf de zeven vragen op een lijstje en neem het mee. Stel ze in dezelfde volgorde, en noteer wat het bureau antwoordt. Aan het einde van het gesprek heb je een veel beter beeld van of dit het juiste bureau is dan je had gekregen door alleen naar het powerpoint-verhaal te luisteren.',
      },
    ],
    relatedArticles: [
      'wat-is-coreprint-methodiek',
      'wat-kost-seo-zakelijke-dienstverlener',
      'waarom-google-ads-data-de-beste-seo-onderbouwing-is',
    ],
  },
  {
    slug: 'google-ads-of-seo-voor-zakelijke-dienstverlener',
    title: 'Google Ads of SEO voor een zakelijke dienstverlener?',
    cluster: 'CorePrint Methodiek',
    metaDescription:
      'De keuze tussen Google Ads en SEO is geen of/of vraag. Voor zakelijke dienstverleners werken ze het beste in elkaars verlengde. Hoe je beide inzet in een geïntegreerde aanpak.',
    content: [
      { type: 'heading', content: 'De vraag die verkeerd gesteld wordt' },
      {
        type: 'paragraph',
        content:
          'Negen van de tien zakelijke dienstverleners stellen de vraag "moet ik investeren in Google Ads of in SEO?". Het is een logische vraag, maar het is verkeerd gesteld. Het is niet of-of. Voor de meeste advieskantoren werkt het juist het beste als ze elkaar versterken, in een gefaseerde aanpak die begint met advertenties en eindigt met gratis verkeer.',
      },
      {
        type: 'callout',
        content:
          'Advertenties en SEO zijn geen alternatieven. Ze zijn twee stappen in dezelfde keten, en als je ze los gebruikt mis je de helft van de waarde.',
      },
      { type: 'heading3', content: 'Wat advertenties wel doen, en wat ze niet doen' },
      {
        type: 'paragraph',
        content:
          'Google Ads zetten je direct in beeld bij zoekers, vanaf dag één. Per klik betaal je tussen de één en tien euro voor zakelijke dienstverlening, soms meer voor sterk competitive niches. Zolang je betaalt, krijg je verkeer. Op de dag dat je stopt met betalen, ben je weg. Advertenties bouwen geen waarde op die je later terugkrijgt. Maar ze geven één ding dat SEO niet kan: harde data over welke zoekwoorden converteren.',
      },
      { type: 'heading3', content: 'Wat SEO wel doet, en wat het niet kan' },
      {
        type: 'paragraph',
        content:
          'SEO bouwt aan organische posities die maand na maand meer waarde opleveren. Een artikel dat vandaag op positie zes staat, kan over zes maanden op positie één staan, met tien keer zoveel verkeer, zonder één cent extra investering. Het probleem: SEO werkt traag. De eerste resultaten duren zes maanden, het echte vliegwiel begint pas na twaalf tot achttien maanden. En SEO werkt alleen als je vooraf de juiste zoekwoorden kiest, wat zonder advertentiedata een gokspel is.',
      },
      { type: 'heading3', content: 'De geïntegreerde aanpak in drie fases' },
      {
        type: 'list',
        items: [
          'Fase 1, maand 1 tot 3: Advertenties als meetinstrument. Je betaalt voor kliks om te ontdekken welke zoekwoorden conversies opleveren. Tussen de honderden en duizenden euro\'s aan media-spend in deze fase, om bewijs te verzamelen.',
          'Fase 2, maand 3 tot 12: Bouwen op bewijs. Je investeert in SEO-content op de gevalideerde converters. Advertenties blijven draaien, maar gerichter en met lagere kosten per lead.',
          'Fase 3, maand 12 en verder: Afbouwen van advertenties. Naarmate je organische posities stijgen, krijg je gratis verkeer op de zoekwoorden waarvoor je eerder betaalde. Het advertentiebudget kan worden gehalveerd, en uiteindelijk minimaal worden gehouden voor remarketing en pieken.',
        ],
      },
      { type: 'heading3', content: 'Waarom de meeste kantoren dit verkeerd doen' },
      {
        type: 'paragraph',
        content:
          'Het patroon dat we vaak zien: een kantoor begint met SEO zonder advertenties, op basis van keyword-tools. Resultaten blijven uit, omdat de zoekwoorden niet bewezen waren. Na zes maanden geeft de eigenaar het op en zet hij in op Google Ads. De ads draaien, maar zonder fundament in de website daaronder is het rendement laag. Door beide om te draaien (eerst meten met ads, dan bouwen met SEO) wordt het traject veel efficiënter.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw kantoor' },
      {
        type: 'paragraph',
        content:
          'Als je nog niet hebt geadverteerd, begin met een kleine testcampagne van een paar honderd euro op vijftien tot twintig zoekwoorden in jouw markt. Drie maanden draaien geeft genoeg data om te zien welke kant je SEO-investering op moet. Veel zekerder dan beginnen op gevoel.',
      },
    ],
    relatedArticles: [
      'seo-vs-advertenties',
      'wat-is-coreprint-methodiek',
      'waarom-google-ads-data-de-beste-seo-onderbouwing-is',
    ],
  },
  {
    slug: 'eigen-website-of-platform-onder-een-holding',
    title: 'Eigen website of platform onder een holding: wat past bij jouw label?',
    cluster: 'B2B Groei & Klantacquisitie',
    metaDescription:
      'Wanneer kies je voor een eigen website voor een sub-label, en wanneer is een sectie op de holding-site beter? Een gids voor advieskantoren die meerdere proposities in de markt willen zetten.',
    content: [
      { type: 'heading', content: 'De keuze die over jaren doorwerkt' },
      {
        type: 'paragraph',
        content:
          'Veel advieskantoren bestaan uit meerdere proposities onder één koepel. Een holding met daarbinnen Overnamehuys, Financieringsgilde, een waarderingsbureau. Een accountantskantoor met daarnaast een fiscaal label en een payroll-tak. Op een gegeven moment komt de vraag op tafel: krijgt elk label een eigen website, of zetten we het allemaal onder één koepel-site? De keuze klinkt klein maar werkt jaren door, zowel in SEO-resultaten als in commerciële groei.',
      },
      {
        type: 'callout',
        content:
          'De juiste keuze hangt af van hoe verschillend de doelgroepen zijn, niet van wat het mooist staat in een organogram.',
      },
      { type: 'heading3', content: 'Wanneer een eigen website beter werkt' },
      {
        type: 'list',
        items: [
          'De doelgroepen zijn fundamenteel verschillend: een waarderings-klant heeft niets te zoeken op een overname-pagina, en omgekeerd. Eigen domeinen voorkomen dat een bezoeker verdwaalt.',
          'Je wilt op specifieke zoektermen ranken: "bedrijfswaardering" als domeinnaam (mijnbedrijfswaarderingen.nl) geeft directe positioneringskracht in Google.',
          'Elk label moet eigen autoriteit opbouwen: voor een sub-label dat zelfstandig moet kunnen leven, is eigen domeinkracht onmisbaar.',
          'Verkoop of afsplitsing is een mogelijkheid: een eigen merk met eigen domein is veel makkelijker te verkopen of af te splitsen dan een sectie op een groter platform.',
        ],
      },
      { type: 'heading3', content: 'Wanneer een sectie op de holding-site volstaat' },
      {
        type: 'list',
        items: [
          'De doelgroepen overlappen sterk: als jouw waarderings-klanten ook jouw overname-klanten zijn, kan één site met goede navigatie volstaan.',
          'Het sub-label heeft beperkte zoekvolumes: voor heel kleine niches loont het soms niet om een eigen domein autoriteit op te bouwen.',
          'De holding heeft al sterke domeinkracht: een sectie onder een autoriteits-site rankt sneller dan een nieuw domein dat helemaal opnieuw moet bouwen.',
          'Beheer en onderhoud zijn een schaarse resource: één site beheren is veel goedkoper dan vier sites parallel onderhouden.',
        ],
      },
      { type: 'heading3', content: 'De combinatie die het beste werkt' },
      {
        type: 'paragraph',
        content:
          'Voor de meeste advieskantoren met meerdere labels werkt een combinatie het beste. De holding krijgt een overkoepelende site die de groep als geheel presenteert. Elk sub-label krijgt een eigen domein met een gerichte propositie, eigen content en eigen funnel. De holding-site linkt naar de sub-labels, en omgekeerd. Zo bouw je vier of vijf domeinen met eigen autoriteit, terwijl bezoekers op het juiste punt in jouw groep landen.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw groep' },
      {
        type: 'paragraph',
        content:
          'Maak een lijst van alle labels onder jouw koepel en kruis aan welke een zelfstandige propositie hebben met een eigen doelgroep. Voor die labels bouw je in de komende jaren een eigen online aanwezigheid op. Voor labels die organisatorisch wel apart zijn maar commercieel naar dezelfde doelgroep gaan, volstaat een sectie op de holding-site.',
      },
    ],
    relatedArticles: [
      'klanten-werven-financieel-adviseur',
      'wat-is-coreprint-methodiek',
      'seo-silo-wat-is-het',
    ],
  },
  {
    slug: 'linkedin-strategie-voor-zakelijke-dienstverleners',
    title: 'LinkedIn-strategie voor zakelijke dienstverleners',
    cluster: 'B2B Groei & Klantacquisitie',
    metaDescription:
      'LinkedIn is voor zakelijke dienstverleners het belangrijkste social-kanaal, maar wordt vaak verkeerd ingezet. Een praktische gids voor adviseurs die LinkedIn willen omzetten in nieuwe opdrachten.',
    content: [
      { type: 'heading', content: 'Waarom LinkedIn voor adviseurs anders werkt dan voor andere ondernemers' },
      {
        type: 'paragraph',
        content:
          'Voor een webshop is LinkedIn vrijwel waardeloos. Voor een adviseur is het het belangrijkste social platform. De reden is simpel: adviseurs verkopen vertrouwen aan zakelijke kopers, en op LinkedIn bouw je dat vertrouwen op via inhoud en zichtbaarheid in jouw professionele netwerk. Maar de meeste adviseurs gebruiken LinkedIn verkeerd: ze posten af en toe, plaatsen vooral nieuws over hun eigen bedrijf, en wachten af. Dat werkt niet.',
      },
      {
        type: 'callout',
        content:
          'Op LinkedIn winnen geen bedrijven, winnen mensen. Het persoonlijke profiel doet zes keer meer dan het bedrijfsprofiel.',
      },
      { type: 'heading3', content: 'Persoonlijk profiel vs bedrijfsprofiel' },
      {
        type: 'paragraph',
        content:
          'Het bedrijfsprofiel is verplicht, maar het werkt nauwelijks. LinkedIn-algoritmes zetten persoonlijke posts veel hoger in de feed dan bedrijfsposts. Voor een adviseur betekent dit dat de partners, senior consultants of accountmanagers het werk moeten doen op hun eigen profiel, niet op het bedrijfsaccount. Het bedrijfsaccount is de footprint, maar de mensen zijn de motor.',
      },
      { type: 'heading3', content: 'Wat een werkende contentritmiek voor adviseurs is' },
      {
        type: 'list',
        items: [
          'Eén inhoudelijke post per week per medewerker, met een concrete inzicht of mening. Niet vier oppervlakkige posts, maar één stevige.',
          'Mix van formats: tekstpost (best presterend), foto-post (goed voor zichtbaarheid), document/carousel (sterk voor langere inhoud).',
          'Reageer dagelijks vijftien minuten op posts van anderen in jouw doelgroep, met inhoudelijke commentaren. Dat bouwt zichtbaarheid op zonder zelf elke dag te posten.',
          'Verwijs af en toe naar je website-content, maar maak het niet promotioneel. Één op zes posts mag een link naar je site bevatten, de rest is pure inhoud.',
        ],
      },
      { type: 'heading3', content: 'Waar de meeste adviseurs op vastlopen' },
      {
        type: 'paragraph',
        content:
          'Het is niet de strategie, het is de consistentie. Bijna elke adviseur weet dat LinkedIn belangrijk is, maar slechts weinigen houden een ritme vol. De truc is om het te koppelen aan iets dat je al doet. Eén keer per week na een goed gesprek met een cliënt, schrijf in tien minuten een korte inzicht-post over wat het gesprek je opviel. Dat is geen extra werk, dat is observatie omzetten in zichtbaarheid.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw praktijk' },
      {
        type: 'paragraph',
        content:
          'Plan deze week vrijdagochtend dertig minuten om je eerste post te schrijven. Onderwerp: een observatie uit een recent cliëntgesprek, geanonimiseerd, met een conclusie waar jouw doelgroep wat aan heeft. Plan voor de komende vier weken alvast de tijdslots in je agenda. Klein beginnen en doorzetten is altijd beter dan groots beginnen en stoppen.',
      },
    ],
    relatedArticles: [
      'klanten-werven-als-consultant',
      'content-frequentie-voor-een-adviespraktijk-realistisch-plan',
      'klanten-werven-financieel-adviseur',
    ],
  },
  {
    slug: 'mail-funnel-die-leads-opwarmt-bij-een-adviespraktijk',
    title: 'Een mail-funnel die leads opwarmt bij een adviespraktijk',
    cluster: 'B2B Groei & Klantacquisitie',
    metaDescription:
      'Veel cliënten van een adviseur zijn nog niet klaar om direct te tekenen. Een doordachte mail-funnel houdt ze warm, bouwt vertrouwen op, en levert opdrachten op het juiste moment.',
    content: [
      { type: 'heading', content: 'Het probleem van te vroege of te late contactmomenten' },
      {
        type: 'paragraph',
        content:
          'Een ondernemer die overweegt zijn bedrijf te verkopen, doet daar gemiddeld zes tot achttien maanden over voordat hij echt contact opneemt. Een DGA die nadenkt over een fiscale herstructurering kan jaren rondlopen met de gedachte. Een ondernemer die zijn website wil vernieuwen, oriënteert zich vaak drie tot zes maanden. Wat betekent dit voor jouw acquisitie? Het betekent dat de meeste mensen die op je website komen, niet meteen klaar zijn om actie te ondernemen. Een goed gesprek of telefoontje is op dat moment te vroeg. Maar zonder vervolgcontact verdwijnen ze in de mist.',
      },
      {
        type: 'callout',
        content:
          'De kunst is niet om iedere bezoeker direct te bellen, maar om met de bezoeker contact te houden totdat hij klaar is.',
      },
      { type: 'heading3', content: 'Wat een mail-funnel concreet inhoudt' },
      {
        type: 'paragraph',
        content:
          'Een mail-funnel is een serie van vooraf geschreven e-mails die automatisch worden verstuurd naar iemand die zich heeft ingeschreven, bijvoorbeeld door een whitepaper of checklist te downloaden. De serie loopt over zes tot twaalf weken en bouwt geleidelijk de relatie op. Elke mail levert waarde, niet een verkooppraatje. Tegen het einde van de serie is de lezer ofwel een geinformeerde prospect die klaar is voor een gesprek, ofwel iemand die afhaakt omdat het niet bij hem past. Beide uitkomsten zijn waardevol.',
      },
      { type: 'heading3', content: 'Wat een werkende mail-funnel voor een adviseur bevat' },
      {
        type: 'list',
        items: [
          'Mail 1, dag 0: De gevraagde whitepaper of checklist, met een korte introductie van jouw kantoor.',
          'Mail 2, dag 3: Een verdiepend artikel over een aspect dat in de whitepaper kort werd aangestipt. Pure waarde, geen verkoop.',
          'Mail 3, dag 7: Een korte case of klantverhaal dat laat zien wat het oplevert om jouw type advies te krijgen. Geanonimiseerd, maar concreet.',
          'Mail 4, dag 14: Een herinnering aan een vaak gemaakte fout in jouw vakgebied, met uitleg hoe het voorkomen kan worden.',
          'Mail 5, dag 21: Een uitnodiging voor een vrijblijvend gesprek, met heldere uitleg wat dat gesprek inhoudt en wat het oplevert.',
          'Mail 6 en verder, eens per twee weken: doorlopende waarde-e-mails met inzichten, nieuwsupdates, en occasionele uitnodigingen.',
        ],
      },
      { type: 'heading3', content: 'Wat absoluut niet werkt' },
      {
        type: 'paragraph',
        content:
          'Een mail-funnel met te veel verkoop in de eerste weken jaagt mensen weg. Een mail-funnel zonder enige uitnodiging om iets te doen levert nooit gesprekken op. De balans zit ergens in het midden: de eerste twee weken zijn pure waarde, vanaf week drie mag er een zachte uitnodiging in, en pas vanaf week zes of zeven mag een directe call to action centraal staan.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw praktijk' },
      {
        type: 'paragraph',
        content:
          'Maak een whitepaper of checklist over een belangrijk onderwerp in jouw vakgebied, een document van tien tot twintig pagina\'s dat waarde levert. Zet daarachter een inschrijfformulier op je website. Begin met een eenvoudige funnel van drie e-mails (welkom, verdieping, uitnodiging) en breid uit naarmate je ziet wat werkt. Niet te complex te beginnen, dat is de val waar veel kantoren in trappen.',
      },
    ],
    relatedArticles: [
      'klanten-werven-financieel-adviseur',
      'linkedin-strategie-voor-zakelijke-dienstverleners',
      'content-frequentie-voor-een-adviespraktijk-realistisch-plan',
    ],
  },
  {
    slug: 'ai-tools-die-een-adviseur-tijd-besparen-zonder-vertrouwen-te-verliezen',
    title: 'AI-tools die een adviseur tijd besparen zonder vertrouwen te verliezen',
    cluster: 'AI & Entity SEO',
    metaDescription:
      'Voor adviseurs zijn AI-tools een tweesnijdend zwaard. Ze besparen tijd, maar verkeerd ingezet beschadigen ze het vertrouwen. Hoe gebruik je AI verantwoord in een adviespraktijk?',
    content: [
      { type: 'heading', content: 'De vraag die elke adviseur zich stelt' },
      {
        type: 'paragraph',
        content:
          'AI-tools zijn niet meer weg te denken uit het werk van een adviseur. ChatGPT voor concept-teksten, Perplexity voor onderzoek, Copilot voor administratieve taken. Tegelijk is er een gerechtvaardigde zorg: als jouw cliënt erachter komt dat zijn fiscale advies of juridische memo deels door een AI is opgesteld, beschadigt dat het vertrouwen. Maar AI niet gebruiken betekent achterlopen op concurrenten die wel tijd besparen. Hoe combineer je het?',
      },
      {
        type: 'callout',
        content:
          'De vraag is niet of je AI gebruikt, maar waar het past en waar het niet past. Die grens helder houden is het hele spel.',
      },
      { type: 'heading3', content: 'Waar AI ongevaarlijk en waardevol is' },
      {
        type: 'list',
        items: [
          'Eerste concepten van interne notities, samenvattingen van vergaderingen, of agendapunten voor cliëntgesprekken.',
          "Onderzoek naar achtergrondinformatie over een sector, een wet, of een trend. AI is sneller in literatuuroverzicht dan handmatige Google's.",
          'Hulp bij het schrijven van marketingcontent, blog-artikelen en LinkedIn-posts, mits je het eindresultaat zelf doorloopt op feitelijke juistheid en toon.',
          'Vertaling van vakjargon naar cliënt-vriendelijke taal in commerciële communicatie.',
        ],
      },
      { type: 'heading3', content: 'Waar AI niet thuishoort' },
      {
        type: 'list',
        items: [
          'Definitief inhoudelijk advies: een fiscaal advies, een juridische memo, of een waarderingsrapport mag nooit door AI worden opgesteld zonder grondige eigen controle.',
          'Cliëntspecifieke berekeningen: AI-hallucinaties bij cijfers zijn een real risk. Voor numerieke uitkomsten altijd handmatig of via gevalideerde tools.',
          'Vertrouwelijke cliëntinformatie zonder bewust gekozen omgeving: standaard AI-tools mogen geen NAW-gegevens of dossiergegevens zien zonder dat je weet waar de data heen gaat.',
          'Vervanging van het cliëntgesprek: AI kan een conceptantwoord opstellen, maar de menselijke beoordeling van de specifieke cliëntsituatie blijft de kern van je werk.',
        ],
      },
      { type: 'heading3', content: 'De praktische werkroutine' },
      {
        type: 'paragraph',
        content:
          'Een werkende routine voor de meeste adviseurs: gebruik AI als eerste denkpartner en als concept-schrijver, maar altijd met een sluitende eigen controle voordat iets de cliënt bereikt. Behandel een AI-output zoals je een eerste concept van een stagiair zou behandelen: een goed startpunt, maar nooit het eindresultaat. Hou interne afspraken vast over wat wel en niet door AI gegenereerd mag worden, en deel die afspraken transparant met cliënten waar relevant.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw praktijk' },
      {
        type: 'paragraph',
        content:
          'Maak deze week intern een lijstje van vijf taken waar AI nu al tijd kan besparen zonder cliëntrisico, en vijf taken waar AI uitgesloten is. Bespreek dit lijstje met je team. Vanaf dat moment heb je een werkbare lijn die meegroeit naarmate AI zelf verbetert en de regels in jouw vakgebied scherper worden.',
      },
    ],
    relatedArticles: [
      'zoektermen-in-2026-hoe-mensen-via-ai-naar-adviseurs-zoeken',
      'seo-voor-ai-tools',
      'content-frequentie-voor-een-adviespraktijk-realistisch-plan',
    ],
  },
  {
    slug: 'zoektermen-in-2026-hoe-mensen-via-ai-naar-adviseurs-zoeken',
    title: 'Zoektermen in 2026: hoe mensen via AI naar adviseurs zoeken',
    cluster: 'AI & Entity SEO',
    metaDescription:
      'Een groeiend aantal cliënten zoekt niet meer via Google, maar via ChatGPT of Perplexity. Hoe veranderen de zoekvragen, en hoe positioneert jouw adviespraktijk zich in dit nieuwe landschap?',
    content: [
      { type: 'heading', content: 'De stille verschuiving in zoekgedrag' },
      {
        type: 'paragraph',
        content:
          'Tot 2024 was Google praktisch het enige zoekkanaal voor zakelijke cliënten. In 2025 en 2026 verschuift dat. Een groeiend aantal ondernemers, vooral jongere of meer technologisch ingestelde, opent eerst ChatGPT of Perplexity om antwoord te krijgen op een vraag, en pas daarna Google. Voor een adviseur betekent dit dat het zoekgedrag van cliënten verandert en dat de manier waarop ze adviseurs vinden ook anders wordt. Wie hier nu op anticipeert, heeft over twee tot vier jaar een aanzienlijke voorsprong.',
      },
      {
        type: 'callout',
        content:
          'AI-zoeken vervangt Google niet, maar het gaat ervoor zitten. De adviseur die in beide gevonden wordt, wint dubbel.',
      },
      { type: 'heading3', content: 'Hoe AI-zoeken anders is dan Google-zoeken' },
      {
        type: 'paragraph',
        content:
          'In Google typt iemand kort en specifiek: "belastingadviseur Alkmaar", "DGA-loon 2026". In ChatGPT of Perplexity typt diezelfde persoon vaak een volle zin met context: "Wat is een redelijk DGA-loon in 2026 voor een holding met een werk-BV in de IT-sector?". Het antwoord dat hij krijgt is geen lijst zoekresultaten, maar een uitgewerkte tekst met soms een paar bronvermeldingen. Of jouw kantoor in die bronvermeldingen verschijnt, bepaalt of je in het AI-tijdperk vindbaar bent.',
      },
      { type: 'heading3', content: 'Wat AI-tools nodig hebben om jouw kantoor te noemen' },
      {
        type: 'list',
        items: [
          'Diepgaande, betrouwbare content op jouw website: AI-modellen wegen content op feitelijke juistheid, structuur en autoriteit. Diepgaande artikelen worden eerder geciteerd dan oppervlakkige pagina\'s.',
          'Vermeldingen van jouw merknaam op andere autoritatieve sites: hoe vaker je in vakbladen, brancheverenigingen of nieuwsmedia genoemd wordt, hoe groter de kans dat een AI je suggesteert.',
          "Gestructureerde data (schema markup) op je website: vertelt zoekmachines en AI expliciet wat voor type pagina dit is, wie de auteur is, en hoe het past in de bredere context.",
          "Heldere positionering en niche: AI-modellen citeren liever specialisten dan generalisten. Een kantoor dat duidelijk maakt waar het in gespecialiseerd is, wordt eerder genoemd.",
        ],
      },
      { type: 'heading3', content: 'Concreet voorbeeld van AI-zoekgedrag' },
      {
        type: 'paragraph',
        content:
          'Een ondernemer in Noord-Holland die zijn bedrijf wil verkopen, kan in Perplexity typen: "Welke overnameadviseurs in Noord-Holland werken specifiek met familiebedrijven in technische sectoren?". Als jouw kantoor op de eigen website specifiek schrijft over familiebedrijfovernames in technische sectoren in Noord-Holland, met diepgaande artikelen en cases, dan is de kans groot dat jouw naam in het antwoord verschijnt. Als je alleen een algemene "overnameadviseur"-pagina hebt, blijf je onzichtbaar.',
      },
      { type: 'heading3', content: 'De eerste stap voor jouw kantoor' },
      {
        type: 'paragraph',
        content:
          'Test deze week zelf: open ChatGPT en Perplexity, en stel drie tot vijf vragen die jouw idealecliënt zou kunnen stellen. Verschijnt jouw kantoor in de antwoorden, of verschijnen alleen concurrenten? Zo niet, dan weet je waar je op moet gaan focussen. Diepgaande content, sterke niche, en consistent gepubliceerde expertise zijn de bouwstenen van AI-vindbaarheid.',
      },
    ],
    relatedArticles: [
      'ai-tools-die-een-adviseur-tijd-besparen-zonder-vertrouwen-te-verliezen',
      'seo-voor-ai-tools',
      'wat-is-entity-seo',
    ],
  },
]
